import { TOTAL_AMOUNT_TARGET_KEY } from '@/context/services/ap_mismatch_alert_settings/utils';
import { useGetApPayeeAccountNumberWithStatus } from '@/context/services/ap_payee/ApPayee.service';
import { useGetReportInferences } from '@/context/services/report_inferences/ReportInferences.service';
import {
  toComparableDueDate,
  type InferenceEntry,
} from '@/context/services/report_inferences/utils';
import { useOcrContext } from '@/features/InvoiceReport/Edit/components/Context/OcrContext';
import { useInferenceAlertState } from '@/features/InvoiceReport/Edit/components/hooks/useInferenceAlertState';
import { type InferenceAlerts } from '@/features/InvoiceReport/Edit/components/ReportFormInputs/ReportFormInputs';
import { PaymentRequestForm } from '@/features/InvoiceReport/Edit/type';
import { useDataExtractionMismatchAlertFlag } from '@/hooks/feature_flag/useDataExtractionMismatchAlertFlag';
import { useMismatchAlert } from '@/hooks/invoice_reports/useMismatchAlert';
import { useCallback, useEffect } from 'react';
import { useWatch, type Control, type UseFormGetValues } from 'react-hook-form';

type UseReportInferenceAlertsParams = {
  reportId: string;
  reportTypeId: string;
  control: Control<PaymentRequestForm>;
  getValues: UseFormGetValues<PaymentRequestForm>;
  payeeInputId?: string;
  dueDateInputId?: string;
  invoiceRegistrationNumberId?: string;
  isActiveInstantPayee?: boolean;
};

export type UseReportInferenceAlertsReturn = {
  inferenceAlerts: InferenceAlerts;
  totalAmountInference: InferenceEntry | undefined;
  isTotalAmountAlertEnabled: boolean;
};

export const useReportInferenceAlerts = ({
  reportId,
  reportTypeId,
  control,
  getValues,
  payeeInputId,
  dueDateInputId,
  invoiceRegistrationNumberId,
  isActiveInstantPayee,
}: UseReportInferenceAlertsParams): UseReportInferenceAlertsReturn => {
  const { reflectedInferences } = useOcrContext();

  const isMismatchAlertFlag = useDataExtractionMismatchAlertFlag();
  const rawReportInferences = useGetReportInferences(
    reportId,
    isMismatchAlertFlag
  );
  const inferences =
    reflectedInferences ?? rawReportInferences?.inferences ?? [];
  const { inferencesMap, isFieldAlertEnabled } = useMismatchAlert(
    inferences,
    reportTypeId
  );

  const payeeFormValue = useWatch({ control, name: 'payee.value' });
  const selectedPayeeId = payeeFormValue || undefined;
  const isPayeeAlertEnabled =
    !!payeeInputId &&
    !isActiveInstantPayee &&
    isFieldAlertEnabled(payeeInputId);
  const shouldLookupPayeeAccountNumber =
    isPayeeAlertEnabled && !!inferencesMap.apPayee;

  const {
    accountNumber: selectedPayeeAccountNumber,
    isLoading: isSelectedPayeeAccountNumberLoading,
  } = useGetApPayeeAccountNumberWithStatus(
    shouldLookupPayeeAccountNumber ? selectedPayeeId : undefined
  );

  const getPayeeComparableValue = useCallback(
    () => selectedPayeeAccountNumber ?? null,
    [selectedPayeeAccountNumber]
  );
  const getDueDateComparableValue = useCallback(
    () => toComparableDueDate(getValues('payee.dueDate')),
    [getValues]
  );
  const getInvoiceRegNumComparableValue = useCallback(() => {
    if (!invoiceRegistrationNumberId) return null;
    const value = getValues('reportForm')?.[invoiceRegistrationNumberId];
    return typeof value === 'string' ? value : null;
  }, [getValues, invoiceRegistrationNumberId]);

  const payee = useInferenceAlertState(
    inferencesMap.apPayee,
    getPayeeComparableValue,
    isPayeeAlertEnabled && !isSelectedPayeeAccountNumberLoading
  );

  const { onBlur: recomputePayeeAlert } = payee;
  useEffect(() => {
    recomputePayeeAlert();
  }, [
    recomputePayeeAlert,
    selectedPayeeAccountNumber,
    isSelectedPayeeAccountNumberLoading,
  ]);

  const dueDate = useInferenceAlertState(
    inferencesMap.dueDate,
    getDueDateComparableValue,
    !!dueDateInputId && isFieldAlertEnabled(dueDateInputId)
  );
  const invoiceRegistrationNumber = useInferenceAlertState(
    inferencesMap.invoiceRegistrationNumber,
    getInvoiceRegNumComparableValue,
    !!invoiceRegistrationNumberId &&
      isFieldAlertEnabled(invoiceRegistrationNumberId)
  );

  return {
    inferenceAlerts: { payee, dueDate, invoiceRegistrationNumber },
    totalAmountInference: inferencesMap.totalAmount,
    isTotalAmountAlertEnabled: isFieldAlertEnabled(TOTAL_AMOUNT_TARGET_KEY),
  };
};
